import type { MouseEvent } from "react";
import { forwardRef, useEffect, useState } from "react";
import { css, styled, ThemeProvider } from "styled-components";

import { UIButton } from "common/components";
import { breakpoints, breakpointValues } from "common/styles";
import { useTranslate } from "common/utilities/useTranslate";
import { IncidentBanner } from "domains/incidents/components/IncidentBanner";
import { AuthButtons } from "domains/landing/components/AuthButtons";
import { StyledLandingTitle } from "domains/landing/components/StyledLandingTitle";
import { darkTheme } from "domains/theme/constants/theme";

const StyledIncidentBanner = styled(IncidentBanner)`
  flex-grow: 0;
  margin-bottom: 6rem;
`;

const StyledBannerContent = styled.div<{ hasLoaded: boolean }>`
  transition:
    opacity 0.3s ease-in-out,
    transform 0.3s ease-in-out;
  position: relative;
  padding: 1rem;
  box-sizing: border-box;
  max-width: 1600px;
  margin: auto;
  ${({ hasLoaded }) => css`
    transform: ${`scale(${hasLoaded ? 1.0 : 1.3})`};
    opacity: ${hasLoaded ? 1 : 0};
  `};

  // TODO these selectors are bad, replace them
  @media (max-width: ${breakpointValues.medium - 1}px) {
    a,
    button {
      width: 75%;
      margin: auto;
      max-width: 250px;

      > div {
        width: 100%;
        justify-content: space-between;
      }
    }
  }
`;

const StyledBannerSubTitle = styled.p`
  font-size: 1.5rem;
  margin: 0 0 1rem 0;
`;

const StyledBannerDescription = styled.p`
  font-size: 0.9rem;
  font-weight: bold;
  margin: 0 0 1rem 0;
`;

const StyledVideoPlayButton = styled(UIButton)`
  width: 100%;

  @media ${breakpoints.medium} {
    width: auto;
  }
`;

type VideoOverlayContentProps = { className?: string; handleWatchVideoClick: (event: MouseEvent) => void };

export const VideoOverlayContent = forwardRef<HTMLDivElement, VideoOverlayContentProps>(
  ({ className, handleWatchVideoClick }, ref) => {
    const translate = useTranslate();
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
      requestAnimationFrame(() => {
        setHasLoaded(true);
      });
    }, []);

    return (
      <ThemeProvider theme={darkTheme}>
        <StyledBannerContent className={className} ref={ref} hasLoaded={hasLoaded}>
          <StyledIncidentBanner />
          <StyledLandingTitle>Strategic Intelligence</StyledLandingTitle>
          <StyledBannerSubTitle>{translate("app.discover.curatedBanner.subtitle")}</StyledBannerSubTitle>
          <StyledBannerDescription>{translate("app.discover.curatedBanner.text")}</StyledBannerDescription>
          <AuthButtons />
          <StyledVideoPlayButton variant="outlined" onClick={handleWatchVideoClick} icon="play">
            {translate("app.discover.curatedBanner.button")}
          </StyledVideoPlayButton>
        </StyledBannerContent>
      </ThemeProvider>
    );
  },
);

VideoOverlayContent.displayName = "VideoOverlayContent";
